import type { Config, DriveStep } from 'driver.js';
import type { Ref } from 'vue';

// API Response Types
export type InteractiveGuideFeature =
  | 'annual_returns'
  | 'registrations'
  | 'user_profile'
  | 'member_management'
  | 'unit_administration'
  | 'general';

export type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
export type PopoverAlign = 'start' | 'center' | 'end';

export interface InteractiveGuideStepResponse {
  id: number;
  sort_order: number;
  element: string;
  title: string | null;
  description: string | null;
  side: PopoverSide | null;
  align: PopoverAlign | null;
  is_draft: boolean;
}

export interface InteractiveGuideResponse {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  feature: InteractiveGuideFeature | null;
  is_draft: boolean;
  steps: InteractiveGuideStepResponse[];
}

export interface InteractiveGuideApiResponse {
  data: InteractiveGuideResponse;
}

// Driver.js Configuration Types
export interface GuideStepPopover {
  title?: string;
  description?: string;
  side?: PopoverSide;
  align?: PopoverAlign;
}

export interface GuideStep extends DriveStep {
  element: string;
  popover: GuideStepPopover;
}

export type GuideDriverConfig = Partial<Config>;

// Store Types
export interface CachedGuide {
  guide: InteractiveGuideResponse | null;
  fetchedAt: number;
}

export interface InteractiveGuidesState {
  guides: Map<string, CachedGuide>;
  loading: Set<string>;
}

// Composable Types
export interface UseInteractiveGuideOptions {
  slug: string;
  autoStart?: boolean;
  driverConfig?: GuideDriverConfig;
  onStart?: () => void;
  onComplete?: () => void;
  onDismiss?: () => void;
  onError?: (error: Error) => void;
  showHoverBadges?: boolean;
  hoverBadgeDelay?: number;
}

export interface UseInteractiveGuideReturn {
  startTour: () => Promise<void>;
  stopTour: () => void;
  isLoading: Ref<boolean>;
  isActive: Ref<boolean>;
  hasCompleted: Ref<boolean>;
  guide: Ref<InteractiveGuideResponse | null>;
  error: Ref<Error | null>;
  resetCompletion: () => void;
  startTourAtStep: (elementSelector: string) => Promise<void>;
  initHoverBadges: () => Promise<void>;
  cleanupHoverBadges: () => void;
}

// Completion Tracking
export const GUIDE_COMPLETION_PREFIX = 'guide_completed_';

export function getCompletionKey(slug: string): string {
  return `${GUIDE_COMPLETION_PREFIX}${slug}`;
}

// Re-export Driver.js types for convenience
export type { Config as DriverConfig, DriveStep } from 'driver.js';
